import { Headline } from '@oneaudi/unified-web-alpha-components';
import { useCallback, useMemo } from 'react';
import { useI18n } from '~/app/i18n/i18n';
import { useMarketConfig } from '~/app/market-config';
import { useVATConfig } from '~/app/market-config/use-vat-config';
import { useMoreInformationDialog } from '~/app/pages/configurator/components/more-information-dialog';
import { appTrackingService } from '~/app/tracking/app-tracking-service';
import { EventAction } from '~/app/tracking/feld-m-types';
import { useDebugMode } from '~/app/utils/debug-mode';
import { SelectionContainer } from '~/components/image-selection-tile/style/tile-selection-section-style';
import { ExtendedSelectionGroupWrapper } from '~/components/selection-card-new/extended-selection-group';
import { useActiveCard } from '~/components/slider-panel';
import { useCatalog, useConfigurator } from '~/graphql/hooks/use-configurator';
import { CatalogItem } from '~/graphql/mapper';
import { FeatureCategory, FeatureOrPackage, SelectableOption, SelectionCardItem } from '~/graphql/types';

export function PaintTypes({ step, total }: { step: number; total: number }) {
	const { i18n } = useI18n();
	const { config } = useMarketConfig();
	const { feature } = useVATConfig();
	const { exteriorPaintTypes } = useCatalog();
	const { isItemLoading, isItemChecked, loadingFlags } = useConfigurator();
	const { openMoreInformationDialog } = useMoreInformationDialog();
	const { selectCard } = useActiveCard();
	const { getName } = useDebugMode();

	const trackSelectionClickEvent = useCallback(
		(
			eventName: string,
			label: string,
			item: FeatureOrPackage,
			elementName: string,
			value: string,
			selected?: boolean,
			eventAction = EventAction.InternalLink,
		) => {
			appTrackingService.trackSelectionClickEvent({
				eventAction,
				eventName,
				label,
				elementName,
				value,
				category: 'paintTypes',
				elementCurrentStep: step,
				elementTotalSteps: total,
				selected,
				item,
			});
		},
		[step, total],
	);

	const isCheckbox = useMemo(
		() => exteriorPaintTypes?.length === 1 && exteriorPaintTypes[0].status.changeable,
		[JSON.stringify(exteriorPaintTypes)],
	);

	const items: SelectionCardItem[] = useMemo(() => {
		// Filter out tech packages and prepare for selection card
		if (!exteriorPaintTypes) return [];

		return exteriorPaintTypes.map((item, index): SelectionCardItem => {
			return {
				name: getName(item) ?? item.name,
				key: item.pr7,
				loading: isItemLoading(item),
				selected: isItemChecked(item),
				readonly: loadingFlags.checkingConflicts || !item.status.available,
				onSelect: () =>
					selectCard(index, item as CatalogItem, () =>
						trackSelectionClickEvent(
							'click to change configuration',
							item.name || '',
							item.__item,
							'card',
							'selection feature app: paintTypes: all',
							isItemChecked(item),
						),
					),
				standard: item.status.standard,
				price: config.showPrices
					? {
							totalPrice: item.totalPrice,
							feature,
						}
					: undefined,

				textLink: {
					label: i18n('ngc.actions.moreInfo'),
					onTextLinkClick: () =>
						openMoreInformationDialog(
							item as SelectableOption<FeatureCategory>,
							item.__item,
							() =>
								trackSelectionClickEvent(
									'click to change configuration',
									item.name || '',
									item.__item,
									'button',
									'details layer',
									isItemChecked(item),
								),
							step,
							total,
							'paintTypes',
						),
				},
				onDescriptionToggle: (showDescription: boolean) =>
					trackSelectionClickEvent(
						`click to ${showDescription ? 'close' : 'open'} details accordion`,
						showDescription ? i18n('ngc.actions.hideDetails') : i18n('ngc.actions.showDetails'),
						item.__item,
						'text link',
						'details accordion',
						undefined,
						EventAction.Content,
					),
				disclaimerContent: item.__item.texts?.disclaimerText || undefined,
				disclaimerHeadline: item.__item.texts?.disclaimerHeadline || undefined,
				disclaimerStartDate: item.__item.texts?.disclaimerStartDate || undefined,
				disclaimerEndDate: item.__item.texts?.disclaimerEndDate || undefined,
			};
		});
	}, [
		config.hideMonthlyRates,
		config.showPrices,
		exteriorPaintTypes,
		feature,
		getName,
		i18n,
		isItemChecked,
		isItemLoading,
		loadingFlags.checkingConflicts,
		openMoreInformationDialog,
		selectCard,
		trackSelectionClickEvent,
		step,
		total,
	]);

	return (
		<>
			<SelectionContainer items={exteriorPaintTypes?.length} tabIndex={0}>
				<Headline className="subtitle" tag="h2" variant={{ order: '4', type: 'headline', weight: 'normal' }}>
					{i18n('ngc.equipment.paintTypes')}
				</Headline>
				<ExtendedSelectionGroupWrapper type={isCheckbox ? 'multi-select' : 'single-select'} items={items} />
			</SelectionContainer>
		</>
	);
}
